import React, { useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Box, IconButton, Stack, Tooltip, Typography, keyframes } from "@mui/material";

const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;

const highlightCellPulse = keyframes`
  0%, 100% { opacity: 0.82; }
  50% { opacity: 0.14; }
`;

const highlightBoundsPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.2; }
`;

/**
 * Glass-ring number pin: translucent white stroke, dark contrast fill,
 * bold white numeral, soft elevation shadow.
 */
export function colorBasketNumberBadgeSx({ size = 20, highlighted = false } = {}) {
    const s = Math.max(14, Number(size) || 20);
    return {
        minWidth: s,
        width: s,
        height: s,
        px: 0,
        py: 0,
        borderRadius: "50%",
        bgcolor: "rgba(18, 18, 20, 0.78)",
        color: "#FFFFFF",
        border: "1.5px solid rgba(255, 255, 255, 0.65)",
        boxShadow: highlighted
            ? "0 0 0 2px rgba(255,255,255,0.28), 0 2px 8px rgba(0,0,0,0.4)"
            : "0 2px 6px rgba(0, 0, 0, 0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: s >= 20 ? 11 : s >= 17 ? 10 : 9,
        fontWeight: 800,
        lineHeight: 1,
        letterSpacing: 0,
        fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        boxSizing: "border-box",
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
        WebkitFontSmoothing: "antialiased",
    };
}

/**
 * Square board mini-map: each piece home cell tinted by its basket.
 * Pieces are exclusive (one basket only), so painted areas never overlap.
 */
export default function AdminColorBasketMiniMap({
    baskets,
    imageUrl,
    grid,
    size = 320,
    highlightBasketIndex = null,
    /** When true with a highlight, dim other baskets. */
    emphasizeOnly = false,
    onBasketClick,
    /** Hide title, zoom controls, and helper caption (play select dialog). */
    compact = false,
    /** Show marker labels as 1…N instead of API 0…N-1. */
    oneBasedLabels = false,
    /** Blink the highlighted basket cells and their outline (capture/promo shots). */
    pulseHighlight = false,
}) {
    const gridN = Number(grid);
    const validGrid = Number.isFinite(gridN) && gridN > 0;
    const hasHighlight = highlightBasketIndex != null && highlightBasketIndex !== "";
    const [zoom, setZoom] = useState(1);
    const baseSize = size == null || size === "" ? null : Number(size);
    const viewportMax = Number.isFinite(baseSize) && baseSize > 0 ? baseSize : undefined;
    const showChrome = !compact;

    const { cellColors, markers, highlightBounds } = useMemo(() => {
        const colors = new Map();
        const sums = new Map();
        let hiMinCol = Infinity;
        let hiMinRow = Infinity;
        let hiMaxCol = -Infinity;
        let hiMaxRow = -Infinity;
        let hiCount = 0;

        if (!Array.isArray(baskets) || !validGrid) {
            return { cellColors: colors, markers: [], highlightBounds: null };
        }

        for (const basket of baskets) {
            const hex = basket?.dominantColorHex || "#999999";
            const index = basket?.index;
            const pieceIds = Array.isArray(basket?.pieceIds) ? basket.pieceIds : [];
            let sumCol = 0;
            let sumRow = 0;
            let n = 0;
            for (const rawId of pieceIds) {
                const id = Number(rawId);
                if (!Number.isInteger(id) || id < 0 || id >= gridN * gridN) continue;
                colors.set(id, { hex, index });
                const col = id % gridN;
                const row = Math.floor(id / gridN);
                sumCol += col + 0.5;
                sumRow += row + 0.5;
                n += 1;
                if (hasHighlight && Number(index) === Number(highlightBasketIndex)) {
                    hiMinCol = Math.min(hiMinCol, col);
                    hiMinRow = Math.min(hiMinRow, row);
                    hiMaxCol = Math.max(hiMaxCol, col);
                    hiMaxRow = Math.max(hiMaxRow, row);
                    hiCount += 1;
                }
            }
            if (n > 0) {
                sums.set(index, {
                    index,
                    hex,
                    cx: sumCol / n / gridN,
                    cy: sumRow / n / gridN,
                    count: n,
                });
            }
        }

        return {
            cellColors: colors,
            markers: [...sums.values()].sort((a, b) => Number(a.index) - Number(b.index)),
            highlightBounds:
                hiCount > 0
                    ? {
                          left: hiMinCol / gridN,
                          top: hiMinRow / gridN,
                          width: (hiMaxCol - hiMinCol + 1) / gridN,
                          height: (hiMaxRow - hiMinRow + 1) / gridN,
                      }
                    : null,
        };
    }, [baskets, gridN, hasHighlight, highlightBasketIndex, validGrid]);

    if (!Array.isArray(baskets) || baskets.length === 0 || !imageUrl || !validGrid) {
        return null;
    }

    const cellPct = 100 / gridN;
    const clickable = typeof onBasketClick === "function";
    const mapPct = `${zoom * 100}%`;
    const fillColumn = !(Number.isFinite(baseSize) && baseSize > 0);

    const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 100) / 100));
    const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 100) / 100));
    const zoomReset = () => setZoom(1);

    return (
        <Stack spacing={compact ? 0 : 1.5}>
            {showChrome ? (
                <>
                    <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, minWidth: 80 }}>
                            Piece map
                        </Typography>
                        <Tooltip title="Zoom out">
                            <span>
                                <IconButton
                                    size="small"
                                    aria-label="Zoom out mini-map"
                                    onClick={zoomOut}
                                    disabled={zoom <= ZOOM_MIN}
                                >
                                    <RemoveIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36, textAlign: "center" }}>
                            {Math.round(zoom * 100)}%
                        </Typography>
                        <Tooltip title="Zoom in">
                            <span>
                                <IconButton
                                    size="small"
                                    aria-label="Zoom in mini-map"
                                    onClick={zoomIn}
                                    disabled={zoom >= ZOOM_MAX}
                                >
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Reset zoom">
                            <span>
                                <IconButton
                                    size="small"
                                    aria-label="Reset mini-map zoom"
                                    onClick={zoomReset}
                                    disabled={zoom === 1}
                                >
                                    <RestartAltIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                        {emphasizeOnly && hasHighlight
                            ? `Highlighted cells are basket ${highlightBasketIndex} piece homes on the board`
                            : "Each cell is one piece in exactly one basket"}
                        {clickable ? " — click a basket marker to practice it" : ""}.
                    </Typography>
                </>
            ) : null}
            <Box
                sx={{
                    width: "100%",
                    ...(viewportMax ? { maxWidth: viewportMax } : null),
                    aspectRatio: "1 / 1",
                    borderRadius: 1,
                    overflow: "auto",
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "grey.900",
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        width: fillColumn ? mapPct : Math.round((viewportMax || 320) * zoom),
                        height: fillColumn ? mapPct : Math.round((viewportMax || 320) * zoom),
                        minWidth: fillColumn ? "100%" : undefined,
                        minHeight: fillColumn ? "100%" : undefined,
                    }}
                >
                    <Box
                        component="img"
                        src={imageUrl}
                        alt="Basket piece map"
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "grid",
                            gridTemplateColumns: `repeat(${gridN}, 1fr)`,
                            gridTemplateRows: `repeat(${gridN}, 1fr)`,
                            pointerEvents: clickable ? "auto" : "none",
                        }}
                    >
                        {Array.from({ length: gridN * gridN }, (_, pieceIndex) => {
                            const entry = cellColors.get(pieceIndex);
                            if (!entry) {
                                return <Box key={pieceIndex} />;
                            }
                            const isHi =
                                hasHighlight && Number(entry.index) === Number(highlightBasketIndex);
                            if (emphasizeOnly && hasHighlight && !isHi) {
                                return <Box key={pieceIndex} />;
                            }
                            return (
                                <Box
                                    key={pieceIndex}
                                    title={`Piece ${pieceIndex} · basket ${entry.index} · (${pieceIndex % gridN},${Math.floor(pieceIndex / gridN)})`}
                                    onClick={
                                        clickable
                                            ? () => onBasketClick(entry.index)
                                            : undefined
                                    }
                                    sx={{
                                        bgcolor: entry.hex,
                                        opacity: isHi ? 0.78 : 0.5,
                                        ...(isHi && pulseHighlight
                                            ? {
                                                  animation: `${highlightCellPulse} 1.1s ease-in-out infinite`,
                                              }
                                            : null),
                                        outline: isHi
                                            ? "2px solid rgba(255,255,255,0.95)"
                                            : "1px solid rgba(0,0,0,0.15)",
                                        outlineOffset: -1,
                                        boxShadow: isHi
                                            ? "inset 0 0 0 1px rgba(0,0,0,0.35)"
                                            : undefined,
                                        cursor: clickable ? "pointer" : "default",
                                    }}
                                />
                            );
                        })}
                    </Box>
                    {highlightBounds ? (
                        <Box
                            aria-hidden
                            sx={{
                                position: "absolute",
                                left: `${highlightBounds.left * 100}%`,
                                top: `${highlightBounds.top * 100}%`,
                                width: `${highlightBounds.width * 100}%`,
                                height: `${highlightBounds.height * 100}%`,
                                border: "2px dashed rgba(255,255,255,0.95)",
                                boxShadow: "0 0 0 1px rgba(0,0,0,0.45)",
                                pointerEvents: "none",
                                zIndex: 4,
                                boxSizing: "border-box",
                                ...(pulseHighlight
                                    ? {
                                          animation: `${highlightBoundsPulse} 1.1s ease-in-out infinite`,
                                      }
                                    : null),
                            }}
                        />
                    ) : null}
                    {markers.map((m) => {
                        const isHi = hasHighlight && Number(m.index) === Number(highlightBasketIndex);
                        if (emphasizeOnly && hasHighlight && !isHi) {
                            return null;
                        }
                        const markerSize = Math.max(16, Math.min(22, cellPct * 2.2)) * Math.min(zoom, 1.5);
                        return (
                            <Box
                                key={m.index}
                                component={clickable ? "button" : "div"}
                                type={clickable ? "button" : undefined}
                                onClick={clickable ? () => onBasketClick(m.index) : undefined}
                                sx={{
                                    position: "absolute",
                                    left: `${m.cx * 100}%`,
                                    top: `${m.cy * 100}%`,
                                    transform: "translate(-50%, -50%)",
                                    ...colorBasketNumberBadgeSx({
                                        size: markerSize,
                                        highlighted: isHi,
                                    }),
                                    zIndex: isHi ? 3 : 2,
                                    cursor: clickable ? "pointer" : "default",
                                    appearance: "none",
                                    WebkitAppearance: "none",
                                }}
                            >
                                {oneBasedLabels ? Number(m.index) + 1 : m.index}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Stack>
    );
}
